/* eslint-disable no-unused-vars */
import React, { useState, useEffect, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useHistory } from 'react-router-dom';

import ProgressBar from 'components/ProgressBar';
import { Version } from 'components/Version';
import { OnboardingProvider } from 'context/onboarding-context';
import { useLocalStorage } from 'hooks/useLocalStorage';
import LoadingScreen from 'screens/LoadingScreen';
import NotFoundScreen from 'screens/NotFoundScreen';

import styles from './RegistrationScreen.module.scss';

const RegistrationScreen = () => {
  // Lazy load all possible registration steps
  const registrationScreens = {
    BuildingSearchScreen: React.lazy(() =>
      import('./registrationScreens/BuildingSearchScreen/BuildingSearchScreen')
    ),
    AgeVerificationScreen: React.lazy(() =>
      import(
        './registrationScreens/AgeVerificationScreen/AgeVerificationScreen'
      )
    ),
    AccountDetailsScreen: React.lazy(() =>
      import('./registrationScreens/AccountDetailsScreen/AccountDetailsScreen')
    ),
    JobRoleScreen: React.lazy(() =>
      import('./registrationScreens/JobRoleScreen/JobRoleScreen')
    ),
    ProfilePhotoScreen: React.lazy(() =>
      import('./registrationScreens/ProfilePhotoScreen/ProfilePhotoScreen')
    ),
    TravelInformationScreen: React.lazy(() =>
      import(
        './registrationScreens/TravelInformationScreen/TravelInformationScreen'
      )
    ),
    BuildingInfoScreen: React.lazy(() =>
      import('./registrationScreens/BuildingInfoScreen/BuildingInfoScreen')
    ),
    TermsAndConditionsScreen: React.lazy(() =>
      import(
        './registrationScreens/TermsAndConditionsScreen/TermsAndConditionsScreen'
      )
    )
  };

  const [registrationSteps] = useState([
    {
      name: 'BuildingSearch',
      title: 'Building information',
      pinRequired: false
    },
    { name: 'AgeVerification', title: 'Age verification', pinRequired: false },
    { name: 'AccountDetails', title: 'Account details', pinRequired: false },
    { name: 'JobRole', title: 'Job role', pinRequired: true },
    {
      name: 'TravelInformation',
      title: 'Travel information',
      pinRequired: true
    },
    { name: 'BuildingInfo', title: 'Building Information', pinRequired: true },
    { name: 'ProfilePhoto', title: 'Profile photo', pinRequired: true },
    {
      name: 'TermsAndConditions',
      title: 'Terms and Conditions',
      pinRequired: true
    }
  ]);
  const [currentStep, setCurrentStep] = useState();
  const [onboardingData] = useLocalStorage('cureoscity_onboarding_data', {});
  const { step } = useParams();
  const [, stepNumber] = step.split('-');
  const history = useHistory();

  const redirectToFirstStep = () => {
    history.push(`/register`);
  };

  const renderStep = () => {
    const { name } = currentStep;
    const StepScreen = registrationScreens[`${name}Screen`];
    return (
      <Suspense fallback={<LoadingScreen heading="Loading..." />}>
        {/* eslint-disable-next-line max-len */}
        {StepScreen ? (
          <OnboardingProvider>
            <StepScreen
              stepNumber={stepNumber}
              redirectToFirstStep={redirectToFirstStep}
            />
          </OnboardingProvider>
        ) : (
          <NotFoundScreen />
        )}
      </Suspense>
    );
  };

  useEffect(() => {
    setCurrentStep(registrationSteps[stepNumber - 1]);
  }, [registrationSteps, stepNumber]);

  const currentTitle = () => registrationSteps[stepNumber - 1]?.title;

  return (
    <>
      <div className={styles.content_container}>
        <Helmet>
          <title>{`Registration - step ${stepNumber} of ${
            registrationSteps.length
          } - ${currentTitle()}`}</title>
        </Helmet>
        <ProgressBar
          step={stepNumber}
          totalSteps={registrationSteps?.length}
          title={currentStep?.title || 'Loading...'}
        />
        <main className={styles.main}>
          <div className={styles.page_contents}>
            {currentStep ? (
              renderStep()
            ) : (
              <LoadingScreen heading="Loading..." />
            )}
          </div>
        </main>
      </div>
      <Version appearance="secondary" />
    </>
  );
};

export default RegistrationScreen;
